import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDelete, onGetAll } from 'redux/actions/documents.actions';
import { documentsSelector } from 'redux/selectors/documents.selector';
import { history } from 'helpers/history';
import { COLORS } from 'helpers/enums/colors';

import LayoutContainer from 'containers/LayoutContainer';
import DocumentsView from 'views/DocumentsView';
import ActionButtons from 'components/commons/Table/components/ActionButtons';
import Dialog from 'components/commons/Dialog';

import { IDocument } from 'types/documents.types';
import { IDocumentType } from 'types/documentsTypes.types';
import { ITableUpdate } from 'components/commons/Table/types';

import 'views/DocumentsView/i18n';

const DocumentsContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<IDocument | undefined>();
  const { count, loading, table } = useTypedSelector((state) => state.documents);
  const items = useTypedSelector(documentsSelector);

  const actions = [
    {
      label: i18next.t<string>('DOCUMENTS_VIEW:SHOW'),
      onClick: (id: string) => history.push(`/documents/${id}/show`),
    },
    {
      label: i18next.t<string>('DOCUMENTS_VIEW:EDIT'),
      onClick: (id: string) => history.push(`/documents/${id}/edit`),
    },
    {
      label: i18next.t<string>('DOCUMENTS_VIEW:DELETE'),
      onClick: (id: string) =>
        setDeleteItem(items.find((item: IDocument) => item.id === Number(id))),
      color: COLORS.redMonza,
    },
  ];

  const columns = [
    {
      name: 'name',
      label: i18next.t<string>('DOCUMENTS_VIEW:NAME'),
    },
    {
      name: 'documentType',
      label: i18next.t<string>('DOCUMENTS_VIEW:DOCUMENT_TYPE'),
      options: {
        customBodyRender: (value: IDocumentType) => value?.name,
        sort: false,
      },
    },
    {
      name: 'file',
      label: i18next.t<string>('DOCUMENTS_VIEW:FILE'),
      options: {
        customBodyRender: (value: string) => (
          <a
            download
            href={value}
            rel="noreferrer"
            target="_blank"
            style={{ color: COLORS.blueRibbon }}
          >
            {i18next.t<string>('DOCUMENTS_VIEW:DOWNLOAD_FILE')}
          </a>
        ),
        sort: false,
      },
    },
    {
      name: 'id',
      label: i18next.t<string>('DOCUMENTS_VIEW:ACTIONS'),
      options: {
        customBodyRender: (value: string) => <ActionButtons id={value} items={actions} />,
        sort: false,
      },
    },
  ];

  const onTableUpdate = useCallback((data: ITableUpdate) => dispatch(onGetAll(data)), [dispatch]);

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDelete(deleteItem.id.toString()));
    setDeleteItem(undefined);
  };

  return (
    <LayoutContainer auth title={i18next.t<string>('DOCUMENTS_VIEW:TITLE')}>
      <DocumentsView
        columns={columns}
        count={count}
        data={items}
        loading={loading}
        onAddClick={() => history.push('/documents/create')}
        onTableUpdate={onTableUpdate}
        table={table}
      />
      <Dialog
        message={i18next.t<string>('DOCUMENTS_VIEW:DELETE_DIALOG_MESSAGE', {
          name: deleteItem?.id,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t<string>('DOCUMENTS_VIEW:DELETE_DIALOG_TITLE')}
      />
    </LayoutContainer>
  );
};

export default DocumentsContainer;
